// Browser push subscribe / unsubscribe. Server-side signing happens in
// netlify/functions/_shared/push.js — this module is the thin client
// surface used by the subscribe UI.

const TRIP_ID_DEFAULT = 'juneau-2026';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export function pushFeatureState() {
  if (typeof window === 'undefined') return { supported: false, reason: 'no-window' };
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  if (!('serviceWorker' in navigator)) return { supported: false, reason: 'no-sw', isIOS, isStandalone };
  if (!('PushManager' in window)) {
    // iOS Safari only exposes PushManager from an installed PWA. Surface
    // a specific code so the UI can render the Add-to-Home-Screen guide.
    if (isIOS && !isStandalone) {
      return { supported: false, reason: 'ios-needs-install', isIOS, isStandalone };
    }
    return { supported: false, reason: 'no-pushmanager', isIOS, isStandalone };
  }
  return { supported: true, isIOS, isStandalone };
}

export async function getPermissionState() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

export async function getCurrentSubscription() {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

async function fetchPublicKey() {
  const res = await fetch('/api/push/public-key');
  if (!res.ok) throw new Error(`/api/push/public-key → ${res.status}`);
  const data = await res.json();
  if (!data.configured) throw new Error('VAPID public key not configured on server');
  return data.key;
}

export async function subscribePush({ tripId = TRIP_ID_DEFAULT, testMode = false } = {}) {
  const reg = await navigator.serviceWorker.ready;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw Object.assign(new Error('notifications denied'), { code: 'permission-denied' });
  }
  const applicationServerKey = urlBase64ToUint8Array(await fetchPublicKey());
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });
  const json = sub.toJSON();
  const res = await fetch('/api/push/subscribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ trip_id: tripId, subscription: json, test_mode: testMode }),
  });
  if (!res.ok) {
    const detail = await res.text();
    const err = new Error(`subscribe failed: ${res.status} ${detail}`);
    err.status = res.status;
    throw err;
  }
  return sub;
}

export async function unsubscribePush({ tripId = TRIP_ID_DEFAULT, testMode = false } = {}) {
  const sub = await getCurrentSubscription();
  if (!sub) return { ok: true };
  await fetch('/api/push/subscribe', {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      trip_id: tripId,
      endpoint: sub.endpoint,
      test_mode: testMode,
    }),
  });
  await sub.unsubscribe();
  return { ok: true };
}

export async function sendTestPush({ tripId = TRIP_ID_DEFAULT, testMode = false } = {}) {
  const res = await fetch('/api/push/test', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ trip_id: tripId, test_mode: testMode }),
  });
  if (!res.ok) {
    const detail = await res.text();
    const err = new Error(`test push failed: ${res.status} ${detail}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
